import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Button,
  Table,
  message,
  Typography,
  Space,
  Alert,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Tabs,
  Spin,
  Select,
  Input,
  Badge,
} from "antd";
const { Option } = Select;
import {
  PlusOutlined,
  DisconnectOutlined,
  LinkOutlined,
  DatabaseOutlined,
  CloudOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  PicqerService,
  type PicqerProduct,
  type ProductImportResult,
} from "../services/picqerService";
import {
  useCompanyProducts,
  type CompanyProduct,
} from "../hooks/useCompanyProducts";
import { Company } from "../types/company";

const { Title, Text } = Typography;

interface PicqerProductImportProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  company: Company;
}

interface ImportItem extends PicqerProduct {
  selected: boolean;
  matchType?: "ean" | "productcode" | "name";
  existingProduct?: Record<string, unknown>;
  isNew: boolean;
}

export const PicqerProductImport: React.FC<PicqerProductImportProps> = ({
  visible,
  onCancel,
  company,
}) => {
  const { t } = useTranslation();
  const { products: companyProducts, refresh: refreshCompanyProducts } =
    useCompanyProducts(company.id);
  const [loading, setLoading] = useState(false);
  const [importData, setImportData] = useState<ProductImportResult | null>(
    null
  );
  const [importItems, setImportItems] = useState<ImportItem[]>([]);
  const [importing, setImporting] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("unlinked");

  // Add useEffect to fetch products when modal opens
  useEffect(() => {
    if (visible && company.picqer_api_key && company.id) {
      handleFetchProducts();
    }
  }, [visible, company.picqer_api_key, company.id]);

  const handleFetchProducts = async () => {
    if (!company.picqer_api_key || !company.id) {
      message.error(t("myProducts.picqerImportModal.fetchStep.apiKeyMissing"));
      return;
    }

    setLoading(true);
    try {
      const result = await PicqerService.importProducts(
        company.picqer_api_key,
        company.id
      );

      if (result.success && result.data) {
        setImportData(result.data);

        // Create import items (new products + matched products for reference)
        const items: ImportItem[] = [
          ...result.data.newProducts.map((product) => ({
            ...product,
            selected: false,
            isNew: true,
          })),
          ...result.data.matchedProducts.map(
            ({ picqerProduct, existingProduct, matchType }) => ({
              ...picqerProduct,
              selected: false,
              matchType,
              existingProduct,
              isNew: false,
            })
          ),
        ];

        setImportItems(items);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error(t("myProducts.picqerImportModal.fetchStep.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleImportSingle = async (picqerProductId: number) => {
    if (!company.picqer_api_key || !company.id) {
      message.error(t("myProducts.picqerImportModal.fetchStep.apiKeyMissing"));
      return;
    }

    setImporting(true);
    try {
      const result = await PicqerService.importProduct(
        company.id!,
        picqerProductId
      );

      if (result.success) {
        message.success(
          t(
            "myProducts.picqerImportModal.selectStep.messages.importSuccessSingle"
          )
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t(
            "myProducts.picqerImportModal.selectStep.messages.importErrorSingle"
          )
        );
      }
    } catch (error) {
      console.error("Error importing product:", error);
      message.error(
        t("myProducts.picqerImportModal.selectStep.messages.importErrorGeneric")
      );
    } finally {
      setImporting(false);
    }
  };

  const handleManualLink = async (
    picqerProductId: number,
    existingProductId: number | null
  ) => {
    if (!existingProductId) {
      message.error(
        t(
          "myProducts.picqerImportModal.selectStep.messages.noExistingProductSelected"
        )
      );
      return;
    }

    setLinking(true);
    try {
      const result = await PicqerService.linkProduct(
        company.id!,
        picqerProductId,
        existingProductId
      );

      if (result.success) {
        message.success(
          t(
            "myProducts.picqerImportModal.selectStep.messages.manualLinkSuccess"
          )
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t("myProducts.picqerImportModal.selectStep.messages.manualLinkError")
        );
      }
    } catch (error) {
      console.error("Error linking product:", error);
      message.error(
        t(
          "myProducts.picqerImportModal.selectStep.messages.manualLinkErrorGeneric"
        )
      );
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkProduct = async (productId: number) => {
    if (!company.picqer_api_key || !company.id) {
      message.error(t("myProducts.picqerImportModal.fetchStep.apiKeyMissing"));
      return;
    }

    setUnlinking(true);
    try {
      const result = await PicqerService.unlinkProduct(company.id!, productId);

      if (result.success) {
        message.success(
          t("myProducts.picqerImportModal.selectStep.messages.unlinkSuccess")
        );
        await refreshCompanyProducts();
        await handleFetchProducts();
      } else {
        message.error(
          t("myProducts.picqerImportModal.selectStep.messages.unlinkError")
        );
      }
    } catch (error) {
      console.error("Error unlinking product:", error);
      message.error(
        t("myProducts.picqerImportModal.selectStep.messages.unlinkErrorGeneric")
      );
    } finally {
      setUnlinking(false);
    }
  };

  const renderProductsTable = () => {
    // Filter products based on search term
    const filteredUnlinkedProducts = importItems.filter((item) => {
      if (!item.existingProduct?.picqer_product_id) {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.name.toLowerCase().includes(searchLower) ||
          (item.ean && item.ean.toString().includes(searchLower)) ||
          (item.barcode && item.barcode.toString().includes(searchLower)) ||
          (item.productcode &&
            item.productcode.toLowerCase().includes(searchLower))
        );
      }
      return false;
    });

    const linkedProducts = companyProducts.filter(
      (product) => product.picqer_product_id
    );

    const unlinkedCompanyProducts = companyProducts.filter(
      (product) => !product.picqer_product_id
    );

    return (
      <div>
        {/* Statistics Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <CloudOutlined style={{ color: "#1890ff" }} />
                    <Text strong>Picqer Products</Text>
                  </Space>
                }
                value={importData?.totalFetched || 0}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <DatabaseOutlined style={{ color: "#52c41a" }} />
                    <Text strong>Your Products</Text>
                  </Space>
                }
                value={companyProducts.length}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <LinkOutlined style={{ color: "#722ed1" }} />
                    <Text strong>Linked</Text>
                  </Space>
                }
                value={linkedProducts.length}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title={
                  <Space>
                    <PlusOutlined style={{ color: "#fa8c16" }} />
                    <Text strong>Available to Import</Text>
                  </Space>
                }
                value={filteredUnlinkedProducts.length}
                valueStyle={{ color: "#fa8c16" }}
              />
            </Card>
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="unlinked"
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
        >
          <Tabs.TabPane
            tab={
              <Badge count={filteredUnlinkedProducts.length} size="small">
                <span>
                  <CloudOutlined style={{ marginRight: 4 }} />
                  Picqer Products
                </span>
              </Badge>
            }
            key="unlinked"
          >
            {filteredUnlinkedProducts.length > 0 ? (
              <div>
                <Input.Search
                  placeholder="Search Picqer products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ marginBottom: 16 }}
                  allowClear
                  prefix={<CloudOutlined />}
                />

                <Table
                  columns={[
                    {
                      title: (
                        <Space>
                          <CloudOutlined />
                          <span>Picqer Product</span>
                        </Space>
                      ),
                      dataIndex: "name",
                      key: "name",
                      render: (name: string, record: ImportItem) => (
                        <Card size="small" style={{ borderColor: "#1890ff" }}>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <Text strong style={{ color: "#1890ff" }}>
                              {name}
                            </Text>
                            {record.productcode && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                Code: {record.productcode}
                              </Text>
                            )}
                            {record.ean && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                EAN: {record.ean}
                              </Text>
                            )}
                            {record.matchType && record.existingProduct && (
                              <Tag color="blue" icon={<LinkOutlined />}>
                                Suggested match:{" "}
                                {
                                  (record.existingProduct as { name: string })
                                    .name
                                }
                              </Tag>
                            )}
                          </Space>
                        </Card>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: 300,
                      render: (_: unknown, record: ImportItem) => (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: "100%" }}
                        >
                          {/* If there's a suggested match, show link button */}
                          {record.matchType && record.existingProduct && (
                            <Button
                              type="default"
                              size="small"
                              icon={<LinkOutlined />}
                              onClick={() =>
                                handleManualLink(
                                  record.idproduct,
                                  (record.existingProduct as { id: number }).id
                                )
                              }
                              loading={linking}
                              style={{ width: "100%" }}
                            >
                              Link to Suggested Match
                            </Button>
                          )}

                          {/* Link to existing product - always available */}
                          <Select
                            showSearch
                            allowClear
                            size="small"
                            style={{ width: "100%" }}
                            placeholder="Link to existing product..."
                            optionFilterProp="children"
                            onChange={(value: number | null) =>
                              handleManualLink(record.idproduct, value)
                            }
                            value={record.existingProduct?.id as number | null}
                            loading={linking}
                            filterOption={(input, option) => {
                              const optionText =
                                option?.children?.toString() || "";
                              return optionText
                                .toLowerCase()
                                .includes(input.toLowerCase());
                            }}
                          >
                            {unlinkedCompanyProducts.map((product) => (
                              <Option key={product.id} value={product.id}>
                                {product.name}{" "}
                                {product.ean ? `(${product.ean})` : ""}
                              </Option>
                            ))}
                          </Select>

                          {/* Import as new product - only show when no product is selected */}
                          {!record.existingProduct?.id && (
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() =>
                                handleImportSingle(record.idproduct)
                              }
                              loading={importing}
                              style={{ width: "100%" }}
                            >
                              Import as New Product
                            </Button>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={filteredUnlinkedProducts}
                  rowKey="idproduct"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <CloudOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                <Title level={4} style={{ marginTop: 16, color: "#666" }}>
                  No Picqer Products Available
                </Title>
                <Text type="secondary">
                  All Picqer products have been imported or linked to your
                  products.
                </Text>
              </div>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <Badge count={linkedProducts.length} size="small">
                <span>
                  <LinkOutlined style={{ marginRight: 4 }} />
                  Linked Products
                </span>
              </Badge>
            }
            key="linked"
          >
            {linkedProducts.length > 0 ? (
              <div>
                <Alert
                  message="Linked Products"
                  description={`${linkedProducts.length} products are currently linked between Picqer and your catalog.`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <Table
                  columns={[
                    {
                      title: (
                        <Space>
                          <DatabaseOutlined />
                          <span>Your Product</span>
                        </Space>
                      ),
                      key: "name",
                      render: (_: unknown, record: CompanyProduct) => (
                        <Card size="small" style={{ borderColor: "#52c41a" }}>
                          <Space direction="vertical" size="small">
                            <Text strong style={{ color: "#52c41a" }}>
                              {record.name}
                            </Text>
                            {record.ean && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                EAN: {record.ean}
                              </Text>
                            )}
                          </Space>
                        </Card>
                      ),
                    },
                    {
                      title: (
                        <Space>
                          <CloudOutlined />
                          <span>Picqer ID</span>
                        </Space>
                      ),
                      dataIndex: "picqer_product_id",
                      key: "picqer_id",
                      render: (id: number) => (
                        <Tag color="blue" icon={<CloudOutlined />}>
                          #{id}
                        </Tag>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: 200,
                      render: (_: unknown, record: CompanyProduct) => (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: "100%" }}
                        >
                          <Button
                            type="link"
                            size="small"
                            icon={<CloudOutlined />}
                            onClick={() => {
                              window.open(
                                `https://e-freight-forwarding.picqer.com/products/${record.picqer_product_id}`,
                                "_blank"
                              );
                            }}
                          >
                            Open in Picqer
                          </Button>
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DisconnectOutlined />}
                            onClick={() => {
                              Modal.confirm({
                                title: "Unlink Product",
                                content: `Are you sure you want to unlink "${record.name}" from Picqer?`,
                                okText: "Unlink",
                                cancelText: "Cancel",
                                okButtonProps: { danger: true },
                                onOk: () => handleUnlinkProduct(record.id),
                              });
                            }}
                            loading={unlinking}
                          >
                            Unlink
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={linkedProducts}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ x: 600 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <LinkOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                <Title level={4} style={{ marginTop: 16, color: "#666" }}>
                  No Linked Products
                </Title>
                <Text type="secondary">
                  No products are currently linked between Picqer and your
                  catalog.
                </Text>
              </div>
            )}
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  };

  const renderStep = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Fetching products from Picqer...</Text>
          </div>
        </div>
      );
    }

    if (importData) {
      return <>{renderProductsTable()}</>;
    }

    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Space direction="vertical" size="large">
          <CloudOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
          <Title level={3}>Import Products from Picqer</Title>
          <Text type="secondary">
            Click the button below to fetch products from your Picqer account
            and see which ones can be imported or linked.
          </Text>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleFetchProducts}
            icon={<SyncOutlined />}
          >
            {loading ? "Fetching Products..." : "Fetch Products from Picqer"}
          </Button>
        </Space>
      </div>
    );
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <CloudOutlined />
          <span>Import Products from Picqer</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1400}
      maskClosable={false}
    >
      {renderStep()}
    </Modal>
  );
};
